import type { ProductFilters } from '#root/api/clients/product/data-contracts'
import type { FilterItem, FiltersMap, PriceFilter, SearchFilter } from '#types/filters'

export default (_filters: MaybeRefOrGetter<ProductFilters>, onChange?: () => void) => {
  const { defaultSort, sorts } = useAppConfig().pages.plp
  const { $gtm, $t } = useNuxtApp()

  const region = useRegion()
  const route = useRoute()
  const router = useRouter()

  const filtersQuery = ref(JSON.parse(route.query.filters?.toString() || '{}'))
  const filters = computed(() => Object.fromEntries(toValue(_filters)
    .map((item) => [item.code, {
      ...item,
      type: item.layout || 'TileExpanded',
      selected: filtersQuery.value[item.code] || []
    }])) as FiltersMap
  )

  // This watch gets triggered both by onFiltersChange
  // and by external changes to the url, such as from links in CMS content
  watch(() => [route.query.filters, route.query.storeFilter, route.query.sort], (value, oldValue) => {
    filtersQuery.value = JSON.parse(route.query.filters?.toString() || '{}')
    if (value[0] !== oldValue?.[0] || value[1] !== oldValue?.[1] || value[2] !== oldValue?.[2])
      onChange?.()
  })

  const sortOptions = sorts[region].map((sort) => ({ value: sort, label: $t.sorts[sort] }))
  const sortState = ref((route.query.sort || defaultSort) as string)
  const storeFilterState = ref(route.query.storeFilter?.toString() || undefined)

  const onFiltersChange = () => {
    const filtersEntries = Object.values(filters.value)
      .filter(({ selected }) => selected?.length)
      .map(({ code, selected }) => [code, selected])

    filtersQuery.value = { ...filtersQuery.value, ...Object.fromEntries(filtersEntries) }

    router.push({
      query: {
        ...route.query,
        filters: Object.keys(filtersQuery.value).length ? JSON.stringify(filtersQuery.value) : undefined,
        sort: sortState.value,
        storeFilter: storeFilterState.value
      }
    })
  }

  const isPriceFilter = (filter: FilterItem): filter is PriceFilter => filter.type === 'PriceInput'
  const isSearchFilter = (filter: FilterItem): filter is SearchFilter => filter.type === 'Search'

  const filterBy = (code: string) => {
    const filter = filters.value[code]
    const { selected, facetId } = filter
    const previous = filtersQuery.value[code] || []

    if (isPriceFilter(filter) || isSearchFilter(filter)) {
      $gtm.push('plpPage.onApplyFilter', code, facetId, selected)
    }
    else if (previous.length < selected.length) {
      const id = selected.filter((item) => !previous.includes(item))[0]
      const { label } = filter.options.find((option) => option.id === id)!
      $gtm.push('plpPage.onApplyFilter', filter.label, facetId, label)
    }
    else {
      const id = previous.filter((item: string) => !selected.includes(item))[0]
      const { label } = filter.options.find((option) => option.id === id)!
      $gtm.push('plpPage.onRemoveFilter', filter.label, facetId, label)
      if (!selected.length) delete filtersQuery.value[code]
    }
    onFiltersChange()
  }

  const filterByStore = (storeId?: string) => {
    storeFilterState.value = storeId
    onFiltersChange()
  }

  const removeFilter = ({ code, id }) => {
    const filter = filters.value[code]
    const removedId = id || filter.selected

    if (isPriceFilter(filter) || isSearchFilter(filter)) {
      $gtm.push('plpPage.onRemoveFilter', code, filter.facetId, removedId)
    }
    else {
      const { label } = filter.options.find((option) => option.id === id)!
      $gtm.push('plpPage.onRemoveFilter', filter.label, filter.facetId, label)
    }

    filtersQuery.value[code] = filter.type === 'PriceInput'
      ? []
      : filtersQuery.value[code]?.filter((item: string) => item !== id)

    if (!filtersQuery.value[code].length) delete filtersQuery.value[code]
    onFiltersChange()
  }

  const clearFilters = () => {
    const selectedFilters = Object.values(filters.value).filter((item) => item.selected?.length)
    const category = selectedFilters?.length === 1 ? selectedFilters[0].label : 'All'

    $gtm.push('plpPage.onClearFilters', category)
    filtersQuery.value = {}
    onFiltersChange()
  }

  const sortBy = () => {
    $gtm.push('plpPage.onSort', sortOptions.find(({ value }) => value === sortState.value)?.label)
    onFiltersChange()
  }

  onActivated(() => {
    // Clear leftover filters in the keep-alive cache when navigating back to the same PLP with a filter-less URL (e.g. breadcrumbs)
    if (!route.query.filters && Object.values(filters.value).some((item) => item.selected?.length))
      clearFilters()

    // update sort and storefilter based on querystring
    sortState.value = (route.query.sort || defaultSort) as string
    storeFilterState.value = route.query.storeFilter?.toString() || undefined
  })

  return {
    clearFilters,
    filterBy,
    filterByStore,
    filters,
    filtersQuery,
    removeFilter,
    sortBy,
    sortOptions,
    sortState,
    storeFilterState
  }
}
